import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Search,
  Funnel,
  Edit2,
  Plus,
  MoreHorizontal,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  X,
  Copy
} from 'lucide-react';
import { useHistory, useLocation } from "react-router-dom";

// Dummy data (10 rows) matching the screenshot exactly
const dummyData = [
  {
    id: 1,
    name: 'Super Admin',
    avatar: 'https://avatar.iran.liara.run/public',
    status: 'Active',
    department: 'IT',
    manager: 'Jane Doe',
    jobTitle: 'IT Manager',
    workplace: 'Mister Mobile HQ'
  },
  {
    id: 2,
    name: 'Admins',
    avatar: 'https://avatar.iran.liara.run/public',
    status: 'Active',
    department: 'IT',
    manager: 'Jane Doe',
    jobTitle: 'IT Manager',
    workplace: 'Mister Mobile HQ'
  },
  {
    id: 3,
    name: 'Manager',
    avatar: 'https://avatar.iran.liara.run/public',
    status: 'Active',
    department: 'IT',
    manager: 'Jane Doe',
    jobTitle: 'IT Manager',
    workplace: 'Mister Mobile HQ'
  },
  {
    id: 4,
    name: 'HR',
    avatar: 'https://avatar.iran.liara.run/public',
    status: 'Active',
    department: 'IT',
    manager: 'Jane Doe',
    jobTitle: 'IT Manager',
    workplace: 'Mister Mobile HQ'
  },
  {
    id: 5,
    name: 'Pricing',
    avatar: 'https://avatar.iran.liara.run/public',
    status: 'Active',
    department: 'IT',
    manager: 'Jane Doe',
    jobTitle: 'IT Manager',
    workplace: 'Mister Mobile HQ'
  },
  {
    id: 6,
    name: 'Logistics',
    avatar: 'https://avatar.iran.liara.run/public',
    status: 'Active',
    department: 'IT',
    manager: 'Jane Doe',
    jobTitle: 'IT Manager',
    workplace: 'Mister Mobile HQ'
  },
  {
    id: 7,
    name: 'Read-Only',
    avatar: 'https://avatar.iran.liara.run/public',
    status: 'Active',
    department: 'IT',
    manager: 'Jane Doe',
    jobTitle: 'IT Manager',
    workplace: 'Mister Mobile HQ'
  },
  {
    id: 8,
    name: 'Product',
    avatar: 'https://avatar.iran.liara.run/public',
    status: 'Active',
    department: 'IT',
    manager: 'Jane Doe',
    jobTitle: 'IT Manager',
    workplace: 'Mister Mobile HQ'
  },
  {
    id: 9,
    name: 'QA',
    avatar: 'https://avatar.iran.liara.run/public',
    status: 'Active',
    department: 'IT',
    manager: 'Jane Doe',
    jobTitle: 'IT Manager',
    workplace: 'Mister Mobile HQ'
  },
  {
    id: 10,
    name: 'QA 2',
    avatar: 'https://avatar.iran.liara.run/public',
    status: 'Active',
    department: 'IT',
    manager: 'Jane Doe',
    jobTitle: 'IT Manager',
    workplace: 'Mister Mobile HQ'
  },
];

const PAGE_SIZE = 10; // Display all 10 rows on one page

export default function PermissionGroup() {
  const [activeTab, setActiveTab] = useState('current-employees');
  const history = useHistory();
  const location = useLocation();

  // Breadcrumbs (you can adjust these if needed)
  const breadcrumbs = ["Permission", "Permission Group"];

  // Search, pagination, and multi‐selection state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);

  // Bulk Edit Drawer (right‐side) state
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const bulkDrawerRef = useRef(null);

  // “Suppress highlight” flag: if true, even checked rows will not be highlighted
  const [suppressHighlight, setSuppressHighlight] = useState(false);

  // Single‐row Drawer state (unchanged)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState('entity-info');
  const [selectedRow, setSelectedRow] = useState(null);

  // Filtered list of rows, based on searchTerm
  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return dummyData.filter(d =>
      d.name.toLowerCase().includes(term) ||
      d.status.toLowerCase().includes(term) ||
      d.department.toLowerCase().includes(term) ||
      d.manager.toLowerCase().includes(term) ||
      d.jobTitle.toLowerCase().includes(term) ||
      d.workplace.toLowerCase().includes(term)
    );
  }, [searchTerm]);

  // Pagination calculations
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageData = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // Toggle selecting a single row (checkbox)
  const toggleSelect = id => {
    setSelectedIds(ids => {
      const newIds = ids.includes(id)
        ? ids.filter(x => x !== id)
        : [...ids, id];

      // As soon as user toggles a checkbox, we should no longer suppress highlight
      setSuppressHighlight(false);
      return newIds;
    });
  };

  // Toggle “select all” on the current page
  const toggleAll = () => {
    setSelectedIds(prev => {
      const next =
        prev.length === pageData.length
          ? []
          : pageData.map(d => d.id);

      // As soon as user toggles “select all,” remove the suppression
      setSuppressHighlight(false);
      return next;
    });
  };

  // Whenever selectedIds changes, open or close the Bulk Edit drawer
  useEffect(() => {
    if (selectedIds.length > 0) {
      setBulkEditOpen(true);
    } else {
      setBulkEditOpen(false);
    }
  }, [selectedIds]);

  // Close Bulk Edit drawer (HIDE it) when clicking outside
  // IMPORTANT: We do NOT clear selectedIds here! We only want to hide it,
  // and suppress row highlighting, until next user checkbox interaction.
  useEffect(() => {
    function handleClickOutsideBulk(event) {
      if (
        bulkEditOpen &&
        bulkDrawerRef.current &&
        !bulkDrawerRef.current.contains(event.target)
      ) {
        setBulkEditOpen(false);
        // **Do not clear `selectedIds` here.** Just suppress highlight until next toggle.
        setSuppressHighlight(true);
      }
    }
    document.addEventListener('mousedown', handleClickOutsideBulk);
    return () => document.removeEventListener('mousedown', handleClickOutsideBulk);
  }, [bulkEditOpen]);

  // When a row (except its checkbox) is clicked, open the single‐row drawer
  const onRowClick = (row) => {
    setSelectedRow(row);
    setDrawerTab('entity-info');
    setDrawerOpen(true);
  };

  // Dropdown (ellipsis) state & refs (unchanged)
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const dropdownRef = useRef(null);
  const buttonRefs = useRef({});
  const drawerRef = useRef(null);

  // Close single‐row Drawer when clicking outside
  useEffect(() => {
    function handleClickOutsideDrawer(event) {
      if (
        drawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target)
      ) {
        setDrawerOpen(false);
        setSelectedRow(null);
        // If we click outside the single‐row drawer, we unselect that row but leave bulk selections alone
        setSelectedIds(ids =>
          selectedRow ? ids.filter(id => id !== selectedRow.id) : ids
        );
      }
    }
    document.addEventListener('mousedown', handleClickOutsideDrawer);
    return () =>
      document.removeEventListener('mousedown', handleClickOutsideDrawer);
  }, [drawerOpen, selectedRow]);

  // Dropdown positioning state
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

   useEffect(() => {
          function handleClickOutside(event) {
              if (
                  dropdownRef.current &&
                  !dropdownRef.current.contains(event.target) &&
                  !buttonRefs.current[dropdownOpenId]?.contains(event.target)
              ) {
                  setDropdownOpenId(null);
              }
          }
  
          if (dropdownOpenId !== null) {
              document.addEventListener("mousedown", handleClickOutside);
          }
  
          return () => {
              document.removeEventListener("mousedown", handleClickOutside);
          };
      }, [dropdownOpenId]);

  // Toggle the per‐row dropdown menu
  const toggleDropdown = (id) => {
    if (dropdownOpenId === id) {
      setDropdownOpenId(null);
    } else {
      const btn = buttonRefs.current[id];
      if (btn) {
        const rect = btn.getBoundingClientRect();
        const dropdownWidth = 150;
        const dropdownHeight = 100;

        let left = rect.left + window.scrollX - dropdownWidth + rect.width;
        if (left < window.scrollX + 8) {
          left = window.scrollX + 8;
        }

        let top = rect.bottom + window.scrollY;
        if (top + dropdownHeight > window.scrollY + window.innerHeight) {
          top = rect.top + window.scrollY - dropdownHeight;
        }

        setDropdownPosition({ top, left });
      }
      setDropdownOpenId(id);
    }
  };

  return (
    <div className="company-body">
      {/* Breadcrumbs */}
      <div className="breadcrumb">
        {breadcrumbs.map((crumb, index) => (
          <span key={index} className="breadcrumb-item">
            {crumb}
            {index !== breadcrumbs.length - 1}
          </span>
        ))}
      </div>

      {/* Permission Card */}
      <div className="info-section">
        <div className="info-card">
          <div className="info-header">
            <h4 className="info-title">Permission</h4>
          </div>

          <div className="entities-tab">
            {/* Toolbar: Search + Filter on left; Edit/Plus on right */}
            <div className="toolbar">
              <div className="toolbar-left">
                <div className="search-box">
                  <Search size={20} />
                  <input
                    type="text"
                    placeholder="Search…"
                    value={searchTerm}
                    onChange={e => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <button className="icon-btn">
                  <Funnel size={18} />
                </button>
              </div>
              <div className="toolbar-right">
                <button
                  className="icon-btn"
                  onClick={() => history.push("/permission-group/add-permission-group")}
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="table-wrapper">
              <table className="entities-table">
                <thead>
                  <tr>
                    <th>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={
                            selectedIds.length === pageData.length && pageData.length > 0
                          }
                          onChange={toggleAll}
                        />
                      </div>
                    </th>
                    <th>
                      <span>
                        Group Name <ChevronsUpDown size={12} />
                      </span>
                    </th>
                    <th>
                      <span>
                        Status <ChevronsUpDown size={12} />
                      </span>
                    </th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map((row, idx) => {
                    // Only highlight a row if:
                    //   • Its checkbox is checked
                    //   • Bulk‐edit drawer is NOT open
                    //   • suppressHighlight is false
                    const isRowSelected =
                      selectedIds.includes(row.id) &&
                      !bulkEditOpen &&
                      !suppressHighlight;

                    return (
                      <tr
                        key={row.id}
                        className={[
                          idx % 2 ? 'striped' : '',
                          isRowSelected ? 'row-selected' : ''
                        ].join(' ')}
                        onClick={() => onRowClick(row)}
                      >
                        <td>
                          <div className="form-check" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedIds.includes(row.id)}
                              onChange={e => {
                                toggleSelect(row.id);
                                if (e.target.checked) {
                                  setSelectedRow(row);
                                } else if (selectedRow?.id === row.id) {
                                  setSelectedRow(null);
                                }
                              }}
                            />
                          </div>
                        </td>
                        <td>
                          <div className="name-cell">
                            <img
                              src={row.avatar}
                              alt="avatar"
                              className="avatar"
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                marginRight: 8,
                                verticalAlign: 'middle'
                              }}
                            />
                            {row.name}
                          </div>
                        </td>
                        <td>
                          <span className={`status ${row.status.toLowerCase()}`}>
                            {row.status}
                          </span>
                        </td>
                        <td style={{ textAlign: "center", position: "relative" }}>
                          <button
                            ref={el => (buttonRefs.current[row.id] = el)}
                            className="icon-btn"
                            onClick={e => {
                              e.stopPropagation();
                              toggleDropdown(row.id);
                            }}
                          >
                            <MoreHorizontal size={20} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                  {/* If there are fewer rows than PAGE_SIZE, fill out empty rows
                                  so that clicking these “tr.s” does nothing (no highlight, no drawer). */}
                  {pageData.length < PAGE_SIZE && (
                    [...Array(PAGE_SIZE - pageData.length)].map((_, idx) => (
                      <tr
                        key={`empty-${idx}`}
                        className="empty-row"
                        onClick={() => {
                          /* Click on empty row: do nothing. */
                        }}
                      >
                        <td colSpan={8}>&nbsp;</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Per‐row dropdown menu */}
              {dropdownOpenId !== null && (
                <div
                  ref={dropdownRef}
                  className="backdrop-cls"
                  style={{
                    position: "absolute",
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    background: "white",
                    boxShadow: "0px 1px 4px 0px #0000001A",
                    zIndex: 1000,
                    minWidth: 152,
                    borderRadius: 8,
                    padding: 12,
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <div className="btn-align-forall">
                  <div className="common-for-all">
                    <button
                      onClick={() => {
                        alert("View Details clicked");
                        setDropdownOpenId(null); // 👈 Close dropdown
                      }}
                    >
                      View Details
                    </button>
                  </div>
                  <div className="common-for-all">
                    <button
                      onClick={() => {
                        alert("Edit clicked");
                        setDropdownOpenId(null); // 👈 Close dropdown
                      }}
                    >
                      Edit
                    </button>
                  </div>
                  <div className="common-for-all">
                    <button
                      onClick={() => {
                        alert("Suspend clicked");
                        setDropdownOpenId(null); // 👈 Close dropdown
                      }}
                      className="deactivate-cls"
                    >
                      Suspend
                    </button>
                  </div>
                </div>
                </div>
              )}
            </div>

            {/* Pagination */}
            {filtered.length > PAGE_SIZE && (
              <div className="pagination">
                <div className="pagination-info">
                  {`Showing ${(currentPage - 1) * PAGE_SIZE + 1} - ${Math.min(currentPage * PAGE_SIZE, filtered.length)} of ${filtered.length} items`}
                </div>
                <div className="pagination-controls">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      className={currentPage === i + 1 ? 'active' : ''}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Single‐row Drawer (unchanged) */}
      {drawerOpen && selectedRow && (
        <>
          <div className="drawer-overlay" />
          <div className="entity-drawer" ref={drawerRef}>
            <div className="drawer-header">
              <h3>Permission Group Info</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setDrawerOpen(false);
                  setSelectedIds(ids => ids.filter(id => id !== selectedRow?.id));
                  setSelectedRow(null);
                }}
              >
                <X size={24} />
              </button>
            </div>
            <div className="main-drawer-inner">
              <div className="drawer-title d-flex align-items-center justify-content-between">
                <div className='d-flex align-items-center'>
                  <img
                    src="https://avatar.iran.liara.run/public"
                    alt="avatar"
                    className="avatar"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      marginRight: 8,
                      verticalAlign: 'middle'
                    }}
                  />
                  <h2 className='pb-0'>Super Admin</h2>
                </div>
                <div className='entities-tab'>
                  <div className='toolbar'>
                    <div className='toolbar-right'>
                      <button className="icon-btn">
                        <Edit2 size={18} />
                      </button>
                      <button className="icon-btn">
                        <MoreHorizontal size={18} />
                      </button>
                    </div>

                  </div>
                </div>
              </div>

              <div className="drawer-tabs">
                <button
                  className={drawerTab === 'entity-info' ? 'active' : ''}
                  onClick={() => setDrawerTab('entity-info')}
                >
                  Basic Info
                </button>
                <button
                  className={drawerTab === 'accessible-apps' ? 'active' : ''}
                  onClick={() => setDrawerTab('accessible-apps')}
                >
                  Accessible Apps
                </button>
                <button
                  className={drawerTab === 'authorized-employees' ? 'active' : ''}
                  onClick={() => setDrawerTab('authorized-employees')}
                >
                  Authorized Employees
                </button>
                <button
                  className={drawerTab === 'additional-info' ? 'active' : ''}
                  onClick={() => setDrawerTab('additional-info')}
                >
                  Additional Info
                </button>
              </div>

              <div className="drawer-content">
                {drawerTab === 'entity-info' && (
                  <div className="tab-panel entity-info-panel">
                    <div className="field-block">
                      <div className="field-label">Name</div>
                      <div className="field-value">{selectedRow.name}</div>
                    </div>

                    <div className="field-block">
                      <div className="field-label">Open ID</div>
                      <div className="field-value d-flex align-items-center gap-2">
                        <span style={{ color: "#2C7BE5", cursor: "pointer", textDecoration: "underline" }} onClick={() => navigator.clipboard.writeText("8d5815b4-38d2-405c-86c0-3366fa7cbccc")}>
                          8d5815b4-38d2-405c-86c0-3366fa7cbccc
                        </span>
                        <Copy size={13} onClick={() => navigator.clipboard.writeText("8d5815b4-38d2-405c-86c0-3366fa7cbccc")} style={{ cursor: "pointer", color: "#2C7BE5" }} />
                      </div>
                    </div>

                    <div className="field-block">
                      <div className="field-label">Status</div>
                      <div className="field-value">
                        <span className={`status ${selectedRow.status.toLowerCase()}`}>
                          {selectedRow.status}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {drawerTab === 'accessible-apps' && (
                  <div className="tab-panel accessible-apps-panel">
                    <div className="field-block">
                      <div className="field-label">Admin</div>
                      <ul className='field-value-listing'>
                        <li className="field-value">Overview</li>
                        <li className="field-value">Organisation Management
                          <ul className='field-value-listing'>
                            <li className="field-value">Company</li>
                            <li className="field-value">Department</li>
                            <li className="field-value">Employee</li>
                          </ul>
                        </li>
                        <li className="field-value">Permission
                          <ul className='field-value-listing'>
                            <li className="field-value">Permission Group</li>
                          </ul>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {drawerTab === 'authorized-employees' && (
                  <div className="tab-panel authorized-employees-panel">
                    <div className="field-block">
                      <div className="field-label">IT</div>
                      <ul className='field-value-listing'>
                        <li className="field-value">
                          <ul className='field-value-listing'>
                            <li className="field-value">Kai</li>
                          </ul>
                        </li>
                      </ul>
                    </div>
                  </div>
                )}

                {drawerTab === 'additional-info' && (
                  <div className="tab-panel additional-info-panel">
                    <p className="note-type-added">Note</p>
                    <label className="no-description-cls when-no-description-cls-adding">No Description</label>
                  </div>
                )}
              </div>

            </div>
          </div>
        </>
      )}
    </div>
  );
};
